import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../components/base';
import { Badge } from '../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { CircleDot, Eye, EyeOff } from 'lucide-react';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { DataTable, IDataTableColumn } from '../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../components/container';
import { BaseShowDetail } from '../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../context/base/eBaseActionStatus';
import { ArticleCategoryModel } from '../../context/models/articles-category/ArticleCategoryModel';
import { ArticleCategoryActionTypes } from '../../context/stores/articles-category/IArticleCategoryActionsTypes';
import { actions } from '../../context/stores/rootActions';
import { AppState } from '../../context/stores/rootReducers';
import ArticleCategoryDetail from './ArticleCategoryDetail';

type IArticleCategoryPageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	allArticleCategorys: ArticleCategoryModel[];
	selectedIds: number[];
	showDetail: BaseShowDetail<ArticleCategoryModel>;
	showConfirm: boolean;
	handleReloadAllData: () => void;
	handleSaveData: (data: ArticleCategoryModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<ArticleCategoryModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `Chuyên mục tin bài`,
		status: state.articleCategory.status,
		allArticleCategorys: state.articleCategory.allArticleCategorys,
		selectedIds: state.articleCategory.selectedIds,
		showDetail: state.articleCategory.showDetail,
		showConfirm: state.articleCategory.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<ArticleCategoryActionTypes>) => ({
	handleReloadAllData: () => {
		dispatch(actions.articleCategory.getAllRequest());
	},
	handleSaveData: (data: ArticleCategoryModel) => {
		dispatch(actions.articleCategory.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.articleCategory.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.articleCategory.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<ArticleCategoryModel>) => {
		dispatch(actions.articleCategory.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.articleCategory.showConfirm(isShow));
	},
});

const ArticleCategoryPage = (props: IArticleCategoryPageProps) => {
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		showDetail,
		allArticleCategorys,
		handleReloadAllData,
		handleDeleteData,
		onChangeSelectedIds,
		handleSaveData,
		onShowDetail,
		onShowConfirm,
	} = props;

	useEffect(() => {
		handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): IDataTableColumn<ArticleCategoryModel>[] => {
		return [
			{ accessorKey: 'name', header: 'Tên', size: 150 },
			{ accessorKey: 'slug', header: 'Slug', minSize: 150 },
			{ 
				accessorKey: 'parentId', 
				header: 'Chuyên mục cha', 
				size: 150,
				cell: ({ getValue }) => {
					const val = getValue() as number;
					const parent = allArticleCategorys.find((g: any) => g.id === val);
					return <span className='font-medium'>{parent ? parent.name : (val || '-')}</span>;
				}
			},
			{ accessorKey: 'keyWords', header: 'Từ khóa', size: 150 },
			{ accessorKey: 'description', header: 'Mô tả', size: 150 },
			{
				accessorKey: 'inActive',
				header: 'Trạng thái',
				size: 100,
				cell: ({ getValue }) => {
					const val = getValue() as boolean;
					return !val ? (
						<Badge variant='outline' className='inline-flex items-center gap-1.5 rounded-full border-emerald-300/70 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700'>
							<CircleDot className='h-3 w-3' />Áp dụng
						</Badge>
					) : (
						<Badge variant='outline' className='inline-flex items-center gap-1.5 rounded-full border-rose-300/70 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-rose-700'>
							<CircleDot className='h-3 w-3' />Không
						</Badge>
					);
				}
			},
			{
				accessorKey: 'isHidden',
				header: 'Ẩn Menu chính',
				size: 90,
				cell: ({ getValue }) => {
					const val = getValue() as boolean;
					return (
						<TooltipProvider delayDuration={120}>
							<Tooltip>
								<TooltipTrigger asChild>
									<span className={!val ? 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-emerald-300/70 bg-emerald-50 text-emerald-700' : 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-amber-300/70 bg-amber-50 text-amber-700'}>
										{!val ? <Eye className='h-3.5 w-3.5' /> : <EyeOff className='h-3.5 w-3.5' />}
									</span>
								</TooltipTrigger>
								<TooltipContent>{!val ? 'Hiển thị menu chính' : 'Ẩn khỏi menu chính'}</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					);
				}
			},
			{
				accessorKey: 'isHiddenSub',
				header: 'Ẩn Menu phụ',
				size: 90,
				cell: ({ getValue }) => {
					const val = getValue() as boolean;
					return (
						<TooltipProvider delayDuration={120}>
							<Tooltip>
								<TooltipTrigger asChild>
									<span className={!val ? 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-emerald-300/70 bg-emerald-50 text-emerald-700' : 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-amber-300/70 bg-amber-50 text-amber-700'}>
										{!val ? <Eye className='h-3.5 w-3.5' /> : <EyeOff className='h-3.5 w-3.5' />}
									</span>
								</TooltipTrigger>
								<TooltipContent>{!val ? 'Hiển thị menu phụ' : 'Ẩn khỏi menu phụ'}</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					);
				}
			},
			{
				accessorKey: 'isHiddenFooter',
				header: 'Ẩn Menu chân trang',
				size: 100,
				cell: ({ getValue }) => {
					const val = getValue() as boolean;
					return (
						<TooltipProvider delayDuration={120}>
							<Tooltip>
								<TooltipTrigger asChild>
									<span className={!val ? 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-emerald-300/70 bg-emerald-50 text-emerald-700' : 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-amber-300/70 bg-amber-50 text-amber-700'}>
										{!val ? <Eye className='h-3.5 w-3.5' /> : <EyeOff className='h-3.5 w-3.5' />}
									</span>
								</TooltipTrigger>
								<TooltipContent>{!val ? 'Hiển thị menu chân trang' : 'Ẩn khỏi menu chân trang'}</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					);
				}
			},
			{
				id: 'actions',
				header: 'Sửa',
				meta: { align: 'center' },
				size: 80,
				cell: ({ row }) => (
					<ButtonLink onClick={() => onShowDetail({ isShow: true, detailData: row.original })} title='Sửa' icon='ni ni-edit' theme='link' />
				)
			}
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allArticleCategorys]);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							{selectedIds.length > 0 && (
								<li>
									<Button
										text={`Xóa (${selectedIds.length})`}
										icon='icon ni ni-trash'
										className='hidden md:inline-flex'
										theme='danger'
										onClick={() => onShowConfirm(true)}
									/>
								</li>
							)}

							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='hidden md:inline-flex'
									onClick={() => onShowDetail({ isShow: true })}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={allArticleCategorys}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination
								totalRecords={allArticleCategorys.length}
								enableGlobalFilter
								toolbar={{
									isShowSearch: true,
									searchPlaceholder: `Tìm kiếm...`
								}}
								onRefresh={handleReloadAllData}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showDetail && showDetail.isShow && (
				<>
					<ArticleCategoryDetail
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						detailModel={showDetail.detailData}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} ${titlePage}`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(ArticleCategoryPage);
